import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Download, AlertTriangle, Brain, GraduationCap, 
  ClipboardCheck, FileSpreadsheet, Loader2, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Reports = () => {
  const [loading, setLoading] = useState({});
  const [activeTab, setActiveTab] = useState('risks');

  const downloadExcelReport = async (endpoint, filename, loadingKey) => {
    setLoading(prev => ({ ...prev, [loadingKey]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/reports/excel/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('content-disposition');
        const serverFilename = contentDisposition?.split('filename=')[1] || filename;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = serverFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast.success('Relatório Excel exportado com sucesso!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao gerar relatório');
      }
    } catch (error) {
      toast.error('Erro ao gerar relatório Excel');
    } finally {
      setLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const tabs = [
    { id: 'risks', label: 'Gestão de Riscos', icon: AlertTriangle },
    { id: 'psi', label: 'Avaliações PSI', icon: Brain },
    { id: 'trainings', label: 'Treinamentos', icon: GraduationCap },
    { id: 'pgr', label: 'Relatórios PGR', icon: ClipboardCheck },
  ];

  const ReportCard = ({ 
    icon: Icon, 
    title, 
    description, 
    features, 
    buttonText, 
    onClick, 
    loadingKey,
    iconBg = 'bg-[#e8f4fc]',
    iconColor = 'text-[#0d9488]'
  }) => (
    <Card className="p-6 rounded-2xl border-0 shadow-[0_4px_20px_rgba(30,58,95,0.05)] hover:shadow-lg transition-all duration-300">
      <div className="flex items-start gap-4">
        <div className={`${iconBg} p-4 rounded-xl`}>
          <Icon size={28} className={iconColor} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {title}
          </h3>
          <p className="text-[#64748b] mb-4 text-sm">
            {description}
          </p>
          <ul className="text-sm text-[#64748b] space-y-1.5 mb-4">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0d9488]"></span>
                {feature}
              </li>
            ))}
          </ul>
          <Button
            onClick={onClick}
            disabled={loading[loadingKey]}
            className="bg-[#0d9488] hover:bg-[#0b7d73] text-white shadow-lg shadow-[#0d9488]/30"
          >
            {loading[loadingKey] ? (
              <Loader2 size={18} className="mr-2 animate-spin" />
            ) : (
              <Download size={18} className="mr-2" />
            )}
            {buttonText}
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h2 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Central de Relatórios
        </h2>
        <p className="text-[#64748b] mt-1">
          Exporte relatórios de conformidade NR-1, auditoria e análises em formato Excel
        </p>
      </div>

      {/* Custom Tabs - Following the image design */}
      <div className="flex justify-center gap-4 py-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative px-8 py-6 rounded-3xl font-semibold text-[#1e3a5f] transition-all duration-300
                ${isActive 
                  ? 'bg-white shadow-[0_8px_30px_rgba(30,58,95,0.15)] scale-105 -translate-y-1' 
                  : 'bg-white/60 hover:bg-white hover:shadow-md'
                }
              `}
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <div className="flex flex-col items-center gap-2">
                <Icon size={24} className={isActive ? 'text-[#0d9488]' : 'text-[#64748b]'} />
                <span className={isActive ? 'text-[#1e3a5f]' : 'text-[#64748b]'}>
                  {tab.label}
                </span>
              </div>
              {isActive && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-[#0d9488] rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Gestão de Riscos */}
        {activeTab === 'risks' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ReportCard
              icon={AlertTriangle}
              title="Inventário de Riscos Ocupacionais"
              description="Relatório completo de riscos identificados com classificação por nível, tipo e matriz de riscos 5x5."
              features={[
                'Lista de riscos por setor',
                'Classificação probabilidade x severidade',
                'Matriz de riscos colorida',
                'Medidas de controle aplicadas'
              ]}
              buttonText="Exportar Riscos (Excel)"
              onClick={() => downloadExcelReport('risks', 'riscos.xlsx', 'risks')}
              loadingKey="risks"
              iconBg="bg-orange-50"
              iconColor="text-orange-600"
            />

            <ReportCard
              icon={FileText}
              title="Planos de Ação"
              description="Relatório de todos os planos de ação para mitigação de riscos ocupacionais."
              features={[
                'Ações por prioridade',
                'Responsáveis e prazos',
                'Status de execução',
                'Histórico de implementação'
              ]}
              buttonText="Exportar Planos (Excel)"
              onClick={() => downloadExcelReport('analytics', 'planos_acao.xlsx', 'action_plans')}
              loadingKey="action_plans"
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
            />
          </div>
        )}

        {/* PSI - Avaliações Psicossociais */}
        {activeTab === 'psi' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ReportCard
              icon={Brain}
              title="Avaliação Psicossocial"
              description="Relatório de avaliações de riscos psicossociais conforme NR-1 com análise de fatores de estresse ocupacional."
              features={[
                'Índice psicossocial por setor',
                'Fatores de risco identificados',
                'Recomendações de intervenção',
                'Comparativo histórico'
              ]}
              buttonText="Exportar PSI (Excel)"
              onClick={() => downloadExcelReport('analytics', 'avaliacao_psi.xlsx', 'psi')}
              loadingKey="psi"
              iconBg="bg-purple-50"
              iconColor="text-purple-600"
            />

            <ReportCard
              icon={ClipboardCheck}
              title="Questionários Aplicados"
              description="Relatório detalhado das respostas dos questionários de avaliação psicossocial."
              features={[
                'Respostas por funcionário',
                'Análise estatística',
                'Gráficos de distribuição',
                'Alertas de risco elevado'
              ]}
              buttonText="Exportar Respostas (Excel)"
              onClick={() => downloadExcelReport('employees', 'questionarios_psi.xlsx', 'psi_responses')}
              loadingKey="psi_responses"
              iconBg="bg-teal-50"
              iconColor="text-teal-600"
            />
          </div>
        )}

        {/* Treinamentos */}
        {activeTab === 'trainings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ReportCard
              icon={GraduationCap}
              title="Controle de Treinamentos"
              description="Relatório completo de treinamentos obrigatórios, matrículas e certificações dos funcionários."
              features={[
                'Lista de treinamentos cadastrados',
                'Status de matrículas',
                'Validade das certificações',
                'Carga horária e NR relacionada'
              ]}
              buttonText="Exportar Treinamentos (Excel)"
              onClick={() => downloadExcelReport('trainings', 'treinamentos.xlsx', 'trainings')}
              loadingKey="trainings"
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
            />

            <ReportCard
              icon={FileSpreadsheet}
              title="Matriz de Capacitação"
              description="Relatório de capacitação por funcionário com status de conformidade NR."
              features={[
                'Treinamentos por funcionário',
                'Certificados vencidos/a vencer',
                'Gap de treinamentos',
                'Cronograma de reciclagem'
              ]}
              buttonText="Exportar Matriz (Excel)"
              onClick={() => downloadExcelReport('employees', 'matriz_capacitacao.xlsx', 'training_matrix')}
              loadingKey="training_matrix"
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
            />
          </div>
        )}

        {/* Relatórios PGR */}
        {activeTab === 'pgr' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ReportCard
              icon={ClipboardCheck}
              title="PGR - Programa de Gerenciamento de Riscos"
              description="Documento completo de conformidade NR-1 com inventário de riscos, plano de ação e grupos de exposição."
              features={[
                'Capa com dados da empresa',
                'Inventário de riscos ocupacionais',
                'Plano de ação com responsáveis',
                'Grupo de exposição similar (GES)'
              ]}
              buttonText="Exportar PGR Completo (Excel)"
              onClick={() => downloadExcelReport('pgr', 'pgr.xlsx', 'pgr')}
              loadingKey="pgr"
              iconBg="bg-[#1e3a5f]/10"
              iconColor="text-[#1e3a5f]"
            />

            <ReportCard
              icon={FileText}
              title="Relatório Analítico SST"
              description="Dashboard analítico com KPIs, distribuição de riscos e status de conformidade da empresa."
              features={[
                'Indicadores chave (KPIs)',
                'Distribuição de riscos por nível',
                'Riscos por tipo ocupacional',
                'Status de conformidade NR-1'
              ]}
              buttonText="Exportar Análise (Excel)"
              onClick={() => downloadExcelReport('analytics', 'analytics.xlsx', 'analytics')}
              loadingKey="analytics"
              iconBg="bg-[#0d9488]/10"
              iconColor="text-[#0d9488]"
            />
          </div>
        )}
      </div>

      {/* Quick Export Section */}
      <Card className="p-6 rounded-2xl border-0 shadow-[0_4px_20px_rgba(30,58,95,0.05)] bg-gradient-to-br from-[#1e3a5f] to-[#0d9488]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="text-white">
            <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Exportação Rápida
            </h3>
            <p className="text-white/80 text-sm">
              Baixe todos os relatórios principais em um clique
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="secondary"
              onClick={() => downloadExcelReport('pgr', 'pgr.xlsx', 'quick_pgr')}
              disabled={loading.quick_pgr}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              {loading.quick_pgr ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ClipboardCheck size={16} className="mr-2" />}
              PGR
            </Button>
            <Button 
              variant="secondary"
              onClick={() => downloadExcelReport('risks', 'riscos.xlsx', 'quick_risks')}
              disabled={loading.quick_risks}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              {loading.quick_risks ? <Loader2 size={16} className="mr-2 animate-spin" /> : <AlertTriangle size={16} className="mr-2" />}
              Riscos
            </Button>
            <Button 
              variant="secondary"
              onClick={() => downloadExcelReport('trainings', 'treinamentos.xlsx', 'quick_trainings')}
              disabled={loading.quick_trainings}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              {loading.quick_trainings ? <Loader2 size={16} className="mr-2 animate-spin" /> : <GraduationCap size={16} className="mr-2" />}
              Treinamentos
            </Button>
          </div>
        </div>
      </Card>

      {/* Info Box */}
      <div className="p-4 bg-[#e8f4fc] rounded-xl border border-[#0d9488]/20">
        <h4 className="font-medium text-[#1e3a5f] mb-2 flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-[#0d9488]" />
          Sobre os Relatórios Excel
        </h4>
        <ul className="text-sm text-[#64748b] space-y-1">
          <li>• Todos os relatórios seguem os padrões de conformidade NR-1 (GRO/PGR)</li>
          <li>• Arquivos formatados profissionalmente com cores da empresa</li>
          <li>• Inclui gráficos, matrizes de risco e estatísticas automatizadas</li>
          <li>• Compatível com Microsoft Excel, Google Sheets e LibreOffice Calc</li>
        </ul>
      </div>
    </div>
  );
};

export default Reports;
